import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

const ROLE_PL = { EDU_INSTITUTION_ADMIN: "Administrator uczelni", EDU_INSTRUCTOR: "Prowadzący",
  EDU_RESEARCHER: "Badacz", EDU_STUDENT: "Student" };
const STATE_PL = { ACTIVE: "AKTYWNE", LIMITED: "OGRANICZONE", REVIEW_REQUIRED: "WYMAGA PRZEGLĄDU",
  SUSPENDED: "ZAWIESZONE" };
const PTYPE_PL = { COURSEWORK: "Ćwiczenia", THESIS: "Praca dyplomowa", RESEARCH_PROJECT: "Projekt badawczy",
  FIELD_STUDY: "Ćwiczenia terenowe", PROFESSIONAL_TRAINING: "Szkolenie", MUNICIPAL_RESEARCH: "Badania samorządowe" };

export default function EduDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [reports, setReports] = useState([]);
  const [dash, setDash] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reportForm, setReportForm] = useState({ parcel_id: "", project_id: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", edu_role: "EDU_STUDENT", cohort: "", exception_justification: "" });
  const [domainErr, setDomainErr] = useState(false);
  const [projForm, setProjForm] = useState({ name: "", type: "COURSEWORK", department: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/edu/me");
      setMe(r.data);
      if (!r.data.membership) return;
      const rep = await api.get("/edu/reports").catch(() => ({ data: { results: [] } }));
      setReports(rep.data.results || []);
      const role = r.data.membership.edu_role;
      if (["EDU_INSTITUTION_ADMIN", "EDU_INSTRUCTOR"].includes(role)) {
        const d = await api.get("/edu/institution/dashboard").catch(() => null);
        if (d) setDash(d.data);
        const mm = await api.get("/edu/institution/members").catch(() => null);
        if (mm) setMembers(mm.data.results || []);
      }
      if (role === "EDU_INSTITUTION_ADMIN") {
        const iv = await api.get("/edu/institution/invites").catch(() => null);
        if (iv) setInvites(iv.data.results || []);
      }
    } catch (ex) { setErr(formatApiError(ex)); }
  }, []);

  useEffect(() => {
    if (user === false) { navigate("/logowanie?returnTo=" + encodeURIComponent("/edu/panel")); return; }
    if (user) load();
  }, [user, navigate, load]);

  if (!user || !me) return <div className="p-20 text-center text-slate-500" data-testid="edu-panel-loading">Ładowanie…</div>;

  if (!me.membership) return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="edu-panel-nomember">
      <Helmet><title>Panel EDU — PlanGmina</title></Helmet>
      <h1 className="text-xl font-bold text-slate-900">Brak przypisania do uczelni</h1>
      <p className="mt-2 text-sm text-slate-600">
        Dostęp EDU aktywuje się zaproszeniem od uczelni. Twoja uczelnia nie uczestniczy jeszcze w programie?
      </p>
      <Link to="/edu/zgloszenie" className="mt-4 inline-block rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white" data-testid="edu-panel-apply-link">
        Zgłoś uczelnię
      </Link>
    </div>);

  const m = me.membership;
  const staff = ["EDU_INSTITUTION_ADMIN", "EDU_INSTRUCTOR", "EDU_RESEARCHER"].includes(m.edu_role);
  const isAdmin = m.edu_role === "EDU_INSTITUTION_ADMIN";
  const canExport = m.state === "ACTIVE";

  const createReport = async (e) => {
    e.preventDefault(); setErr(null); setMsg(null);
    try {
      const r = await api.post("/edu/reports", reportForm);
      navigate(`/edu/raport/${r.data.id}`);
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  const createInvite = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const payload = { ...inviteForm, email: inviteForm.email || null,
        cohort: inviteForm.cohort || null };
      if (!inviteForm.exception_justification?.trim()) delete payload.exception_justification;
      const r = await api.post("/edu/institution/invites", payload);
      setDomainErr(false);
      if (r.data.exception_status === "PENDING") {
        setMsg("Zaproszenie zapisane — adres poza domeną uczelni oczekuje na zatwierdzenie wyjątku przez operatora PlanGmina. Link aktywuje się po zatwierdzeniu.");
      } else {
        setMsg(`Zaproszenie utworzone. Link: /edu/zaproszenie/${r.data.token}`);
      }
      setInviteForm({ email: "", edu_role: "EDU_STUDENT", cohort: "", exception_justification: "" });
      load();
    } catch (ex) {
      if (ex?.response?.data?.detail?.code === "EDU_DOMAIN_MISMATCH") {
        setDomainErr(true);
        setErr(ex.response.data.detail.message);
      } else { setErr(formatApiError(ex)); }
    }
  };

  const createProject = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      await api.post("/edu/institution/projects",
        { ...projForm, department: projForm.department || null });
      setProjForm({ name: "", type: "COURSEWORK", department: "" });
      setMsg("Projekt utworzony."); load();
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  const setMemberState = async (mid, state) => {
    const reason = window.prompt("Powód zmiany stanu (zapisywany w audycie):");
    if (!reason) return;
    try {
      await api.post(`/edu/institution/members/${mid}/state`, { state, reason });
      load();
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  const revokeInvite = async (iid) => {
    try { await api.post(`/edu/institution/invites/${iid}/revoke`); load(); }
    catch (ex) { setErr(formatApiError(ex)); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="edu-dashboard">
      <Helmet><title>Panel EDU — PlanGmina</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">Panel PlanGmina EDU</h1>
      {err && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="edu-panel-error">{err}</p>}
      {msg && <p className="mt-3 break-all rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="edu-panel-msg">{msg}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <section className="rounded-xl border border-slate-300 p-4" data-testid="edu-membership-card">
          <h2 className="text-sm font-bold uppercase text-slate-500">Twoje członkostwo</h2>
          <p className="mt-2 text-sm"><b>{me.institution?.name}</b> ({me.institution?.status})</p>
          <p className="mt-1 text-sm">Rola: <b>{ROLE_PL[m.edu_role]}</b></p>
          <p className="mt-1 text-sm">Stan konta: <b data-testid="edu-member-state">{STATE_PL[m.state] || m.state}</b></p>
          {m.state === "REVIEW_REQUIRED" && (
            <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="edu-review-note">
              Eksporty wstrzymane do czasu przeglądu przez uczelnię. Odczyt danych działa normalnie.
            </p>)}
          <p className="mt-2 text-xs text-slate-500">Warunki EDU: {m.terms_version} · licencja: EDU_NONCOMMERCIAL</p>
        </section>

        <section className="rounded-xl border border-slate-300 p-4" data-testid="edu-usage-card">
          <h2 className="text-sm font-bold uppercase text-slate-500">Limity i zużycie (dziś)</h2>
          <p className="mt-2 text-sm">Wyszukiwania działek: <b>{me.usage_today.lookups} / {me.limits.lookups_per_day}</b></p>
          <p className="mt-1 text-sm">Eksporty PDF: <b>{me.usage_today.exports} / {me.limits.exports_per_day}</b> (miesiąc: {me.exports_month} / {me.limits.exports_per_month})</p>
          {(me.institution?.capabilities || []).includes("ACADEMIC_RESEARCH_EXTENDED") && (
            <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="edu-extended-badge">Rozszerzony dostęp badawczy aktywny</p>)}
          <p className="mt-2 text-xs text-slate-500">
            Zastosowanie komercyjne wymaga osobnej licencji —{" "}
            <Link to="/cennik" className="underline" data-testid="edu-upgrade-link">przejdź do oferty komercyjnej</Link>.
            Historia akademicka pozostaje nietknięta.
          </p>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-projects">
        <h2 className="text-sm font-bold uppercase text-slate-500">Projekty</h2>
        {(me.projects || []).length === 0 && <p className="mt-2 text-sm text-slate-500">Brak przypisanych projektów — poproś prowadzącego o przypisanie.</p>}
        <ul className="mt-2 divide-y divide-slate-100">
          {(me.projects || []).map((p) => (
            <li key={p.id} className="py-2 text-sm" data-testid={`edu-project-${p.id}`}>
              <b>{p.name}</b> <span className="text-slate-500">· {PTYPE_PL[p.type] || p.type}{p.department ? ` · ${p.department}` : ""}</span>
              {p.assigned_parcels?.length > 0 && <span className="ml-2 text-xs text-slate-400">{p.assigned_parcels.length} działek przypisanych</span>}
            </li>
          ))}
        </ul>
        {staff && (
          <form onSubmit={createProject} className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3" data-testid="edu-project-create">
            <input required placeholder="Nazwa projektu" value={projForm.name}
              onChange={(e) => setProjForm({ ...projForm, name: e.target.value })}
              data-testid="edu-project-name" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select value={projForm.type} onChange={(e) => setProjForm({ ...projForm, type: e.target.value })}
              data-testid="edu-project-type" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {Object.entries(PTYPE_PL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input placeholder="Wydział (opcjonalnie)" value={projForm.department}
              onChange={(e) => setProjForm({ ...projForm, department: e.target.value })}
              data-testid="edu-project-dept" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button type="submit" data-testid="edu-project-submit"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Utwórz projekt</button>
          </form>)}
      </section>

      <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-report-create-section">
        <h2 className="text-sm font-bold uppercase text-slate-500">Nowy raport akademicki</h2>
        <form onSubmit={createReport} className="mt-3 flex flex-wrap items-end gap-2" data-testid="edu-report-create">
          <select required value={reportForm.project_id}
            onChange={(e) => setReportForm({ ...reportForm, project_id: e.target.value })}
            data-testid="edu-report-project" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">— wybierz projekt —</option>
            {(me.projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input required placeholder="Numer działki (TERYT)" value={reportForm.parcel_id}
            onChange={(e) => setReportForm({ ...reportForm, parcel_id: e.target.value })}
            data-testid="edu-report-parcel" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button type="submit" disabled={!canExport && false} data-testid="edu-report-submit"
            className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800">
            Generuj raport akademicki
          </button>
        </form>
        <p className="mt-2 text-xs text-slate-500">Raport akademicki zawiera trwały znak wodny i proweniencję źródeł. To nie jest raport due diligence.</p>
      </section>

      <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-reports-list">
        <h2 className="text-sm font-bold uppercase text-slate-500">Moje raporty akademickie</h2>
        {reports.length === 0 && <p className="mt-2 text-sm text-slate-500">Brak raportów.</p>}
        <ul className="mt-2 divide-y divide-slate-100">
          {reports.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2 text-sm" data-testid={`edu-report-${r.id}`}>
              <span><b>{r.parcel_id}</b> <span className="text-slate-400">· {r.created_at?.slice(0, 16).replace("T", " ")}</span></span>
              <Link to={`/edu/raport/${r.id}`} className="text-red-700 underline" data-testid={`edu-report-open-${r.id}`}>Otwórz</Link>
            </li>
          ))}
        </ul>
      </section>

      {isAdmin && (
        <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-invites">
          <h2 className="text-sm font-bold uppercase text-slate-500">Zaproszenia</h2>
          <form onSubmit={createInvite} className="mt-3 flex flex-wrap items-end gap-2" data-testid="edu-invite-create">
            <input type="email" placeholder="E-mail (opcjonalnie)" value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              data-testid="edu-invite-email" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select value={inviteForm.edu_role} onChange={(e) => setInviteForm({ ...inviteForm, edu_role: e.target.value })}
              data-testid="edu-invite-role" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="EDU_STUDENT">Student</option>
              <option value="EDU_INSTRUCTOR">Prowadzący</option>
              <option value="EDU_RESEARCHER">Badacz</option>
            </select>
            <input placeholder="Grupa/kohort" value={inviteForm.cohort}
              onChange={(e) => setInviteForm({ ...inviteForm, cohort: e.target.value })}
              data-testid="edu-invite-cohort" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button type="submit" data-testid="edu-invite-submit"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Utwórz zaproszenie</button>
          </form>
          <p className="mt-2 text-xs text-slate-500" data-testid="edu-invite-domain-hint">
            Zaproszenia kierowane są domyślnie na adresy służbowe w domenie uczelni
            {(me.institution?.domains || []).length ? ` (${me.institution.domains.join(", ")})` : ""}.
            Inny adres wymaga uzasadnienia i zatwierdzenia wyjątku przez operatora PlanGmina.
          </p>
          {domainErr && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="edu-invite-exception-block">
              <label className="text-xs font-semibold uppercase text-amber-800">Uzasadnienie wyjątku domenowego (min. 10 znaków)</label>
              <textarea rows={2} value={inviteForm.exception_justification}
                onChange={(e) => setInviteForm({ ...inviteForm, exception_justification: e.target.value })}
                data-testid="edu-invite-exception" className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm" />
              <p className="mt-1 text-xs text-amber-800">Wyjątek zatwierdza operator PlanGmina przed aktywacją zaproszenia; decyzja jest odnotowywana w dzienniku audytowym.</p>
            </div>)}
          <ul className="mt-3 divide-y divide-slate-100">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center justify-between py-2 text-xs" data-testid={`edu-invite-${i.id}`}>
                <span className="break-all">{ROLE_PL[i.edu_role]} {i.email ? `· ${i.email}` : ""} ·
                  {i.accepted_at ? " przyjęte" : i.revoked_at ? " cofnięte"
                    : i.exception_status === "PENDING" ? " oczekuje na zatwierdzenie wyjątku"
                    : i.exception_status === "DENIED" ? " wyjątek odrzucony" : " aktywne"}
                  {i.exception_status === "APPROVED" ? " · wyjątek zatwierdzony" : ""} ·
                  <code className="text-slate-400">/edu/zaproszenie/{i.token}</code></span>
                {!i.accepted_at && !i.revoked_at && (
                  <button onClick={() => revokeInvite(i.id)} data-testid={`edu-invite-revoke-${i.id}`}
                    className="ml-2 text-red-700 underline">Cofnij</button>)}
              </li>))}
          </ul>
        </section>)}

      {(isAdmin || m.edu_role === "EDU_INSTRUCTOR") && (
        <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-members">
          <h2 className="text-sm font-bold uppercase text-slate-500">Członkowie</h2>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead><tr className="border-b border-slate-200 text-slate-500">
                <th className="py-1 pr-2">E-mail</th><th className="pr-2">Rola</th><th className="pr-2">Stan</th>
                {isAdmin && <th>Akcje</th>}
              </tr></thead>
              <tbody>
                {members.map((mm) => (
                  <tr key={mm.id} className="border-b border-slate-100" data-testid={`edu-member-${mm.id}`}>
                    <td className="py-1 pr-2">{mm.email}</td>
                    <td className="pr-2">{ROLE_PL[mm.edu_role]}</td>
                    <td className="pr-2">{STATE_PL[mm.state] || mm.state}</td>
                    {isAdmin && (
                      <td className="space-x-2">
                        {mm.state !== "SUSPENDED" && <button onClick={() => setMemberState(mm.id, "SUSPENDED")} data-testid={`edu-member-suspend-${mm.id}`} className="text-red-700 underline">Zawieś</button>}
                        {mm.state !== "ACTIVE" && <button onClick={() => setMemberState(mm.id, "ACTIVE")} data-testid={`edu-member-activate-${mm.id}`} className="text-emerald-700 underline">Aktywuj</button>}
                      </td>)}
                  </tr>))}
              </tbody>
            </table>
          </div>
        </section>)}

      {dash && (
        <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-institution-dashboard">
          <h2 className="text-sm font-bold uppercase text-slate-500">Użycie i zgodność — uczelnia</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><p className="text-2xl font-bold">{dash.members_total}</p><p className="text-xs text-slate-500">użytkowników</p></div>
            <div><p className="text-2xl font-bold">{dash.reports_30d}</p><p className="text-xs text-slate-500">raportów (30 dni)</p></div>
            <div><p className="text-2xl font-bold">{dash.exports_30d}</p><p className="text-xs text-slate-500">eksportów (30 dni)</p></div>
            <div><p className="text-2xl font-bold">{dash.projects_total}</p><p className="text-xs text-slate-500">projektów</p></div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 border-t border-slate-100 pt-3 text-xs">
            <p>Otwarte przeglądy: <b data-testid="edu-dash-reviews">{dash.open_reviews.length}</b></p>
            <p>Zablokowane próby komercyjne: <b data-testid="edu-dash-blocked">{dash.blocked_commercial_attempts}</b></p>
            <p>Przekroczenia limitów: <b>{dash.limit_exceeded_events}</b></p>
          </div>
          {dash.open_reviews.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="edu-dash-review-list">
              {dash.open_reviews.map((r) => (
                <li key={r.id} className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                  {r.reason} — użytkownik {r.user_id.slice(0, 8)}… ({r.created_at.slice(0, 10)})
                </li>))}
            </ul>)}
        </section>)}
    </div>
  );
}
